/**
 * generateRoomsReport
 *
 * Builds a print-ready HTML document listing every room with its occupants,
 * plus a trailing "pending — not yet assigned" section, opens it in a new
 * window and triggers the browser's print dialog (Save as PDF).
 *
 * @param {object} camp — the camp row (uses name)
 * @param {object} data — output of buildRoomsReportData(rooms, assignments, regs)
 */
import i18n from '../i18n'

export function generateRoomsReport(camp, data) {
  const t = i18n.t.bind(i18n)

  function esc(s) {
    return String(s ?? '')
      .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;').replaceAll("'", '&#39;')
  }

  const generatedDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  })

  function ageRange(minAge, maxAge) {
    if (minAge == null && maxAge == null) return esc(t('admin.roomsAgeAny'))
    return `${minAge ?? 0}–${maxAge ?? '∞'}`
  }

  const s = data.summary

  const summaryCards = [
    { label: t('admin.roomsSummaryAssigned'), value: s.assigned, bg: '#f0fdf4', bd: '#bbf7d0', fg: '#15803d' },
    { label: t('admin.roomsSummaryPending'), value: s.pending, bg: '#fffbeb', bd: '#fde68a', fg: '#92400e' },
    { label: t('admin.roomsSummaryPendingMale'), value: s.pendingMale, bg: '#eff6ff', bd: '#bfdbfe', fg: '#1e40af' },
    { label: t('admin.roomsSummaryPendingFemale'), value: s.pendingFemale, bg: '#fdf2f8', bd: '#fbcfe8', fg: '#9d174d' },
  ].map(c => `
    <div style="flex:1;min-width:150px;background:${c.bg};border:1px solid ${c.bd};border-radius:12px;padding:16px 20px;">
      <p style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:${c.fg};margin-bottom:4px;">${esc(c.label)}</p>
      <p style="font-size:26px;font-weight:800;color:${c.fg};">${c.value}</p>
    </div>`).join('')

  const roomBlocks = data.rooms.map(room => {
    const occupants = room.occupants.length
      ? `<ol style="margin:10px 0 0 20px;padding:0;">${room.occupants.map(o => `
          <li style="padding:3px 0;color:#374151;">
            ${esc(o.name)}${o.cancelled ? ` <span style="color:#b91c1c;font-weight:600;">${esc(t('admin.roomsCancelledFlag'))}</span>` : ''}
            <span style="color:#9ca3af;"> — ${o.age == null ? '—' : o.age} · ${esc(o.sex)}</span>
          </li>`).join('')}</ol>`
      : `<p style="margin:8px 0 0;font-size:12px;color:#9ca3af;">${esc(t('admin.roomsReportNoOccupants'))}</p>`
    return `
      <div style="page-break-inside:avoid;margin-bottom:20px;padding:16px 20px;border:1px solid #e5e7eb;border-radius:12px;">
        <div style="display:flex;align-items:baseline;justify-content:space-between;flex-wrap:wrap;gap:8px;">
          <p style="margin:0;font-size:15px;font-weight:700;color:#111827;">${esc(room.name)}</p>
          <p style="margin:0;font-size:12px;color:#6b7280;">
            ${room.occupantCount} / ${room.capacity} &nbsp;·&nbsp; ${esc(room.sex)} &nbsp;·&nbsp; ${ageRange(room.minAge, room.maxAge)} ${esc(t('admin.roomsReportColAgeRange'))}
          </p>
        </div>
        ${occupants}
      </div>`
  }).join('')

  const pendingBlock = data.pending.length
    ? `<ol style="margin:10px 0 0 20px;padding:0;">${data.pending.map(p => `
        <li style="padding:3px 0;color:#374151;">${esc(p.name)}<span style="color:#9ca3af;"> — ${p.age == null ? '—' : p.age} · ${esc(p.sex)}</span></li>`).join('')}</ol>`
    : `<p style="margin:8px 0 0;font-size:12px;color:#9ca3af;">${esc(t('admin.roomsReportNoPending'))}</p>`

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${esc(t('admin.roomsReportTitle'))} — ${esc(camp?.name ?? '')}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color:#111827; background:#fff; padding:32px 40px; font-size:14px; line-height:1.5; }
    h2 { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:#374151; margin:28px 0 12px; }
    @page { margin: 20mm 16mm; size: A4; }
    @media print { .no-print { display:none !important; } body { padding:0; font-size:12px; } }
  </style>
</head>
<body>
  <div class="no-print" style="margin-bottom:28px;display:flex;align-items:center;gap:14px;padding:16px 20px;background:#eff1eb;border:1px solid #c0cab6;border-radius:12px;">
    <button onclick="window.print()" style="background:#64795c;color:#fff;border:none;padding:10px 22px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;">
      ${esc(t('admin.reportSaveAsPdf'))}
    </button>
    <span style="font-size:13px;color:#6b7280;">${esc(t('admin.reportSaveAsPdfHint'))}</span>
  </div>

  <div style="border-bottom:3px solid #3d4f3b;padding-bottom:20px;margin-bottom:24px;">
    <div style="display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:12px;">
      <div>
        <p style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#64795c;margin-bottom:4px;">Oasis Joven Camp</p>
        <h1 style="font-size:22px;font-weight:800;color:#111827;margin-bottom:4px;">${esc(t('admin.roomsReportTitle'))}</h1>
        <p style="font-size:15px;font-weight:600;color:#374151;">${esc(camp?.name ?? '')}</p>
      </div>
      <div style="text-align:right;">
        <p style="font-size:11px;color:#9ca3af;margin-bottom:2px;">${esc(t('admin.reportGenerated'))}</p>
        <p style="font-size:13px;font-weight:600;color:#374151;">${generatedDate}</p>
      </div>
    </div>
  </div>

  <div style="display:flex;gap:14px;margin-bottom:8px;flex-wrap:wrap;">${summaryCards}</div>

  <h2>${esc(t('admin.roomsReportOccupantsHeader'))}</h2>
  ${roomBlocks || `<p style="font-size:12px;color:#9ca3af;">${esc(t('admin.roomsReportNoOccupants'))}</p>`}

  <h2 style="border-top:2px solid #f3f4f6;padding-top:24px;">${esc(t('admin.roomsReportPendingSection'))}</h2>
  ${pendingBlock}

  <div style="border-top:1px solid #f3f4f6;padding-top:16px;margin-top:36px;">
    <p style="font-size:11px;color:#9ca3af;text-align:center;">Oasis Joven Camp &nbsp;·&nbsp; ${esc(camp?.name ?? '')} &nbsp;·&nbsp; ${generatedDate}</p>
  </div>
</body>
</html>`

  const win = window.open('', '_blank')
  if (!win) {
    // eslint-disable-next-line no-alert
    alert(t('admin.reportPopupBlocked'))
    return
  }
  win.document.write(html)
  win.document.close()
  setTimeout(() => win.print(), 300)
}
